import type { CSSProperties, ReactNode } from 'react';
import { navigation, semanticColor } from '@beautyburst/tokens';
import { BRAND, Logo, SkipToContent, Surface } from './brand.tsx';

/** Stable page-title ID shared by portal sections and the shell header. */
export const PORTAL_PAGE_TITLE_ID = 'portal-page-title';

export interface NavItem {
  key: string;
  label: string;
  href: string;
  /** Marks the active item so assistive technology announces it. */
  current?: boolean;
  /** Permission required to see this item; enforced again server-side. */
  permission?: string;
}

export interface CustomerShellProps {
  logoSrc?: string;
  headerActions?: ReactNode;
  bottomNav?: readonly NavItem[];
  children: ReactNode;
}

/** Customer marketplace shell: premium beauty retail, mobile-first. */
export function CustomerShell({
  logoSrc,
  headerActions,
  bottomNav = [],
  children,
}: CustomerShellProps) {
  return (
    <Surface surface="customer">
      <SkipToContent />
      <header
        data-bb-component="customer-header"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem',
          background: navigation.customerHeader.background,
          borderBottom: `1px solid ${navigation.customerHeader.border}`,
          minHeight: navigation.customerHeader.height,
          padding: '0 1rem',
        }}
      >
        <Logo src={logoSrc} />
        <p style={{ margin: 0, fontSize: '0.8125rem', color: semanticColor.textSecondary }}>
          {BRAND.customerPromise}
        </p>
        <div
          data-bb-component="customer-header-actions"
          style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', minWidth: 0 }}
        >
          {headerActions}
        </div>
      </header>
      <main
        id="main"
        tabIndex={-1}
        style={{ padding: '1rem', maxWidth: '1200px', margin: '0 auto' }}
      >
        {children}
      </main>
      {bottomNav.length === 0 ? null : (
        <nav
          aria-label="Primary"
          data-bb-component="customer-bottom-nav"
          style={{
            position: 'sticky',
            bottom: 0,
            display: 'flex',
            justifyContent: 'space-around',
            background: navigation.customerBottomNav.background,
            borderTop: `1px solid ${navigation.customerBottomNav.border}`,
            minHeight: navigation.customerBottomNav.height,
          }}
        >
          {bottomNav.map((item) => (
            <a
              key={item.key}
              href={item.href}
              aria-current={item.current === true ? 'page' : undefined}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                minHeight: '44px',
                padding: '0 0.75rem',
                color:
                  item.current === true
                    ? navigation.customerBottomNav.activeText
                    : navigation.customerBottomNav.text,
                textDecoration: 'none',
                fontSize: '0.8125rem',
                fontWeight: 600,
              }}
            >
              {item.label}
            </a>
          ))}
        </nav>
      )}
    </Surface>
  );
}

export interface PortalShellProps {
  surface: 'retailer' | 'admin';
  title: string;
  nav: readonly NavItem[];
  logoSrc?: string;
  headerActions?: ReactNode;
  children: ReactNode;
}

/** Operational shell for retailer and administrator portals. */
export function PortalShell({
  surface,
  title,
  nav,
  logoSrc,
  headerActions,
  children,
}: PortalShellProps) {
  return (
    <Surface surface={surface}>
      <SkipToContent />
      {/*
        Layout lives in base.css so it can respond to viewport width: inline
        styles cannot express a media query, and the fixed-width sidebar left the
        admin console only ~126px of content width at 390px, forcing horizontal
        overflow on every table and metric screen. The sidebar width token is
        handed to CSS as a custom property so the token stays authoritative.
      */}
      <div
        data-bb-component="portal-shell"
        style={
          {
            '--bb-portal-sidebar-width': navigation.portalSidebar.width,
          } as CSSProperties
        }
      >
        <aside
          data-bb-component="portal-sidebar"
          style={{
            background: navigation.portalSidebar.background,
            color: navigation.portalSidebar.text,
            padding: '1.5rem 1rem',
          }}
        >
          <Logo src={logoSrc} variant="mark" />
          <nav
            aria-label={`${surface} navigation`}
            style={{ marginTop: '1.5rem', display: 'grid', gap: '0.25rem' }}
          >
            {nav.map((item) => (
              <a
                key={item.key}
                href={item.href}
                aria-current={item.current === true ? 'page' : undefined}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  minHeight: '44px',
                  padding: '0 0.75rem',
                  borderRadius: '0.625rem',
                  backgroundColor:
                    item.current === true
                      ? navigation.portalSidebar.activeBackground
                      : 'transparent',
                  color:
                    item.current === true
                      ? navigation.portalSidebar.activeText
                      : navigation.portalSidebar.text,
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                }}
              >
                {item.label}
              </a>
            ))}
          </nav>
        </aside>
        {/*
          minWidth:0 is required on this flex item and on <main> below. A flex item
          defaults to min-width:auto, so wide content (a portal data table) grew
          the whole content column past the viewport at 390px instead of letting
          the table's own overflow-x:auto scroll inside it.
        */}
        <div
          data-bb-component="portal-content"
          style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}
        >
          <header
            data-bb-component="portal-header"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: navigation.portalHeader.background,
              borderBottom: `1px solid ${navigation.portalHeader.border}`,
              minHeight: navigation.portalHeader.height,
              padding: '0 1.25rem',
            }}
          >
            <h1
              id={PORTAL_PAGE_TITLE_ID}
              style={{ margin: 0, fontSize: '1.125rem', color: semanticColor.textPrimary }}
            >
              {title}
            </h1>
            <div style={{ display: 'flex', gap: '0.5rem' }}>{headerActions}</div>
          </header>
          <main id="main" style={{ padding: '1.25rem', flex: 1, minWidth: 0 }}>
            {children}
          </main>
        </div>
      </div>
    </Surface>
  );
}
